import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Folder,
  ListTodo,
  Factory,
  Search,
  ChevronDown,
  ChevronRight,
  Check,
  X,
  Map as MapIcon,
  Workflow,
  Plus,
  AlertTriangle,
  TrendingUp,
  CheckCircle2,
  Clock,
  Users,
  UserCheck,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabaseClient';
import { useOrgMembers } from '../hooks/useOrgMembers';
import { useDataScope } from '../hooks/useDataScope';
import MemoModule from '../components/MemoModule';
import { useAcoesRibbon } from '../hooks/useAcoesRibbon';
import type { AcoesViewMode } from '../hooks/useAcoesRibbon';
import { EnterpriseActionsList } from '../components/EnterpriseActionsList';
import { ModuleLayout } from '../components/layout/ModuleLayout';
import { PageHeader } from '../components/layout/PageHeader';
import type { StatusCapsuleItem } from '../components/layout/StatusCapsule';
import { useProjectStore } from '../store/useProjectStore';
import { useProjectContext } from '../context/ProjectContext';
import { useOrganizationId } from '../contexts/WorkspaceContext';
import { useAppToast } from '../components/AppToast';
import { ActionService, type EnterpriseAction, type ActionStatus } from '../services/actionService';
import { ProjectService } from '../services/projectService';
import { ProjectPlanService } from '../services/projectPlanService';
import type { PlanType, ProjectPlan } from '../types/projectPlan';
import { saveProjectToDisk, loadProjectFromDisk } from '../utils/fileSystem';
import { downloadTemplate } from '../utils/downloadTemplate';
import { exportService } from '../services/exportService';
import { generateId } from '../utils/vsmGeometry';
import { desktopRibbonInputClass } from '../components/ribbon/DesktopRibbonControls';
import type { Action, Project } from '../types';
import { NewActionModal } from '../modules/actions/components/NewActionModal';

type ActionRouteMode = 'list' | 'kanban' | 'matrix' | 'dashboard' | 'new';

type ImportedActionRow = Record<string, string | number | null | undefined>;

function getRouteMode(pathname: string): ActionRouteMode {
  if (pathname === '/acoes/nova') return 'new';
  if (pathname === '/acoes/kanban') return 'kanban';
  if (pathname === '/acoes/dashboard') return 'dashboard';
  if (pathname === '/acoes/matriz') return 'matrix';
  return 'list';
}

function normalizeStatus(value: unknown): Action['status'] {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (['doing', 'em andamento', 'em_andamento', 'in_progress', 'andamento'].includes(normalized)) {
    return 'doing';
  }
  if (['done', 'concluido', 'concluído', 'completed', 'finalizado'].includes(normalized)) {
    return 'done';
  }
  if (['canceled', 'cancelado', 'cancelled'].includes(normalized)) {
    return 'canceled';
  }
  return 'todo';
}

function normalizeLevel(value: unknown): 1 | 2 | 3 | undefined {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return undefined;
  if (numeric <= 1) return 1;
  if (numeric >= 3) return 3;
  return 2;
}

async function importActionsFromExcel(file: File, projectId: string): Promise<Action[]> {
  const XLSX = await import('xlsx');
  const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  const firstSheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[firstSheetName];
  const rows = XLSX.utils.sheet_to_json<ImportedActionRow>(worksheet, { defval: '' });

  return rows
    .map((row) => {
      const title = String(
        row.Titulo ??
        row['Título'] ??
        row.Title ??
        row.Acao ??
        row['Ação'] ??
        row.Atividade ??
        row['O Que'] ??
        '',
      ).trim();

      if (!title) return null;

      const description = String(row.Descricao ?? row['Descrição'] ?? row.Description ?? '').trim();
      const responsible = String(row.Responsavel ?? row['Responsável'] ?? row.Quem ?? '').trim();
      const deadline = String(row.Prazo ?? row.Quando ?? row.Deadline ?? '').trim();
      const impacto = normalizeLevel(row.Impacto);
      const esforco = normalizeLevel(row.Esforco ?? row['Esforço']);
      const now = new Date().toISOString();

      return {
        id: generateId(),
        title,
        description: description || undefined,
        status: normalizeStatus(row.Status),
        responsible: responsible || undefined,
        responsavel: responsible || undefined,
        deadline: deadline || undefined,
        prazo: deadline || undefined,
        projectId,
        impacto,
        esforco,
        priority: impacto || esforco ? { impacto, esforco } : undefined,
        impact: impacto ? (impacto === 1 ? 16 : impacto === 2 ? 50 : 83) : undefined,
        effort: esforco ? (esforco === 1 ? 16 : esforco === 2 ? 50 : 83) : undefined,
        createdAt: now,
        updatedAt: now,
      } satisfies Action;
    })
    .filter((action): action is Action => action !== null);
}

function persistProjects(projects: Project[]) {
  localStorage.setItem('lean_workflow_projects', JSON.stringify(projects));
}

const ENTERPRISE_STATUS_MAP: Record<string, Action['status']> = {
  pending: 'todo',
  in_progress: 'doing',
  completed: 'done',
  blocked: 'todo',
  cancelled: 'canceled',
};

const LOCAL_TO_ENTERPRISE_STATUS: Record<string, ActionStatus> = {
  todo: 'pending',
  doing: 'in_progress',
  done: 'completed',
  canceled: 'cancelled',
};

function enterpriseToAction(ea: EnterpriseAction): Action {
  const linkedNodeId = typeof ea.metadata?.linkedNodeId === 'string' ? ea.metadata.linkedNodeId : undefined;
  const source = String(ea.metadata?.source ?? ea.metadata?.generatedFrom ?? '');
  const assignedName = ea.assigned_name ?? (typeof ea.metadata?.assignedName === 'string' ? ea.metadata.assignedName : null);
  const metadataEffortRaw = ea.metadata?.matrixEffort;
  const metadataImpactRaw = ea.metadata?.matrixImpact;
  const metadataEffort = typeof metadataEffortRaw === 'number'
    ? metadataEffortRaw
    : typeof metadataEffortRaw === 'string' && metadataEffortRaw.trim() !== ''
      ? Number(metadataEffortRaw)
      : undefined;
  const metadataImpact = typeof metadataImpactRaw === 'number'
    ? metadataImpactRaw
    : typeof metadataImpactRaw === 'string' && metadataImpactRaw.trim() !== ''
      ? Number(metadataImpactRaw)
      : undefined;
  const originSource = linkedNodeId
    ? (source.includes('postit') || source.includes('idea') ? 'PostIt' : 'Canvas')
    : 'Manual';
  const effort = Number.isFinite(ea.effort as number)
    ? (ea.effort as number)
    : Number.isFinite(metadataEffort as number)
      ? metadataEffort
      : undefined;
  const impact = Number.isFinite(ea.impact as number)
    ? (ea.impact as number)
    : Number.isFinite(metadataImpact as number)
      ? metadataImpact
      : undefined;
  const effortLevel = effort == null
    ? undefined
    : effort <= 3
      ? (effort as 1 | 2 | 3)
      : effort < 33
        ? 1
        : effort < 66
          ? 2
          : 3;
  const impactLevel = impact == null
    ? undefined
    : impact <= 3
      ? (impact as 1 | 2 | 3)
      : impact < 33
        ? 1
        : impact < 66
          ? 2
          : 3;

  return {
    id: ea.id,
    title: ea.title,
    description: ea.description ?? undefined,
    status: ENTERPRISE_STATUS_MAP[ea.status] ?? 'todo',
    responsible: assignedName ?? undefined,
    responsavel: assignedName ?? undefined,
    responsibleUserId: ea.assigned_to ?? undefined,
    deadline: ea.due_date ?? undefined,
    prazo: ea.due_date ?? undefined,
    projectId: ea.project_id ?? undefined,
    _projectName: ea.project_name ?? undefined,
    effort,
    impact,
    esforco: effortLevel,
    impacto: impactLevel,
    linkedNodeId,
    sourceComponentId: typeof ea.metadata?.sourceComponentId === 'string' ? ea.metadata.sourceComponentId : undefined,
    originSource,
    createdAt: ea.created_at,
    updatedAt: ea.updated_at,
  };
}

export const AcoesPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const urlPlanId = searchParams.get('planId');
  // ── Plan View Mode ────────────────────────────────────
  const planProjectId = searchParams.get('project_id');
  const actionFocusParam = searchParams.get('actionFocus');
  const actionFocusColumn = actionFocusParam === 'responsavel' || actionFocusParam === 'prazo' ? actionFocusParam : undefined;
  const isPlanView = !!planProjectId;
  // isActivePlan: either a project-based plan OR a standalone plan via ?planId=
  const isActivePlan = !!planProjectId || !!urlPlanId;
  const planFilterParam = searchParams.get('filter'); // e.g. 'critico'
  const queryClient = useQueryClient();
  const autoCleanupSignatureRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const exportRootRef = useRef<HTMLDivElement | null>(null);
  // Prevents the auto-redirect effect from firing when the user explicitly navigates to /acoes
  const suppressRedirectRef = useRef(false);
  const routeMode = getRouteMode(location.pathname);
  const { showToast, ToastContainer } = useAppToast();
  const organizationId = useOrganizationId();
  const { user: currentUser } = useAuth();
  const [ribbonSearchText, setRibbonSearchText] = React.useState('');
  const [selectedSector, setSelectedSector] = React.useState('all');
  const [selectedResponsible, setSelectedResponsible] = React.useState('all');
  const [selectedStatus, setSelectedStatus] = React.useState('all');
  const [actionScope, setActionScope] = React.useState<'current' | 'all' | 'specific'>(
    isPlanView ? 'specific' : 'current'
  );
  const [specificProjectId, setSpecificProjectId] = React.useState<string | null>(
    planProjectId ?? null
  );
  const [showPlanNewActionModal, setShowPlanNewActionModal] = React.useState(false);
  const [showNewPlanModal, setShowNewPlanModal] = React.useState(false);
  const [kanbanDensity, setKanbanDensity] = React.useState<'1col' | '2col' | '3col'>('1col');
  // Multi-select de equipe — modal Novo Plano
  const [planTeamMembers, setPlanTeamMembers] = React.useState<string[]>([]);
  const [planTeamSearch, setPlanTeamSearch] = React.useState('');
  // Edição de plano existente (Arquivo → Propriedades do Plano...)
  const [editingPlan, setEditingPlan] = React.useState<ProjectPlan | null>(null);

  // ── Enterprise projects (Supabase) for plan navigation ──
  const { data: enterpriseProjects = [] } = useQuery({
    queryKey: ['enterprise-projects-list', organizationId],
    queryFn: () => ProjectService.list(organizationId),
    staleTime: 60_000,
    enabled: !!organizationId,
  });

  // ── Detect if planProject has a linked VSM map (blueprint_usage) ──
  const { data: planProjectMapId = null } = useQuery({
    queryKey: ['plan-project-map', planProjectId, organizationId],
    queryFn: async () => {
      const { data } = await supabase
        .from('process_blueprint_usage')
        .select('blueprint_id')
        .eq('project_id', planProjectId!)
        .eq('organization_id', organizationId)
        .limit(1)
        .maybeSingle();
      return data?.blueprint_id ?? null;
    },
    staleTime: 120_000,
    enabled: !!planProjectId && !!organizationId,
  });

  // ── Org members for Equipe de Execução multi-select ──
  const { members: orgMembers } = useOrgMembers(organizationId);

  // ── Hierarchical data scope (filters actions by reports_to chain) ──
  const { scope: dataScope } = useDataScope();

  // ── Fetch standalone plan details when urlPlanId is set ──
  const { data: activePlan = null } = useQuery({
    queryKey: ['active-plan', urlPlanId, organizationId],
    queryFn: () => ProjectPlanService.getPlan(organizationId, urlPlanId!),
    enabled: !!urlPlanId && !!organizationId,
    staleTime: 60_000,
  });

  // ── Standalone plans (no project) for filter dropdown ──
  const { data: standalonePlans = [] } = useQuery({
    queryKey: ['standalone-plans', organizationId],
    queryFn: () => ProjectPlanService.listStandalonePlans(organizationId),
    enabled: !!organizationId,
    staleTime: 60_000,
  });

  // ── Enterprise mode: check if Supabase actions exist ──
  const { data: _enterpriseActionCount = 0 } = useQuery({
    queryKey: ['enterprise-action-count', organizationId],
    queryFn: () => ActionService.countOverdue(organizationId).then(overdue => overdue).catch(() => 0),
    staleTime: 60_000,
    enabled: !!organizationId,
  });

  const { data: enterpriseActions = [] } = useQuery({
    // Include actionScope so switching to 'all' always triggers a fresh unfiltered fetch,
    // even when urlPlanId/planProjectId were already null from a prior navigation.
    queryKey: ['enterprise-actions', organizationId, actionScope, urlPlanId, planProjectId, dataScope?.type, dataScope?.user_id],
    queryFn: async () => {
      // 'all' scope: no project / plan filter → returns every org action.
      if (actionScope === 'all') {
        return ActionService.list(organizationId, {}, dataScope);
      }
      return ActionService.list(
        organizationId,
        {
          ...(urlPlanId ? { plan_id: urlPlanId } : {}),
          ...(planProjectId ? { project_id: planProjectId } : {}),
        },
        dataScope,
      );
    },
    staleTime: 30_000,
    enabled: !!organizationId && (!urlPlanId || !!activePlan),
  });

  // ── Plan KPIs (derived from enterprise actions when in plan view) ──
  const planKpis = useMemo(() => {
    if (!isActivePlan) return null;
    const actions = enterpriseActions;
    const total = actions.length;
    const done = actions.filter(a => a.status === 'completed').length;
    const now = Date.now();
    const overdue = actions.filter(a =>
      a.status !== 'completed' && a.status !== 'cancelled' &&
      a.due_date && new Date(a.due_date).getTime() < now
    ).length;
    const progress = total > 0 ? Math.round((done / total) * 100) : 0;
    const status = overdue > 0 ? 'critical' : progress < 80 ? 'risk' : 'ok';
    return { total, done, overdue, progress, status };
  }, [isActivePlan, enterpriseActions]);

  useEffect(() => {
    if (!organizationId || !planProjectId || enterpriseActions.length === 0) return;

    const statusWeight = (status: EnterpriseAction['status']) => {
      if (status === 'completed') return 4;
      if (status === 'in_progress') return 3;
      if (status === 'pending') return 2;
      if (status === 'blocked') return 1;
      return 0;
    };
    const isVsmGeneratedAction = (action: EnterpriseAction): boolean => {
      const metadata = action.metadata ?? {};
      const origin = String((metadata as any).origin ?? '').toLowerCase();
      const source = String((metadata as any).source ?? '').toLowerCase();
      const generatedFrom = String((metadata as any).generatedFrom ?? '').toLowerCase();
      const sourceComponentId = String((metadata as any).sourceComponentId ?? '').trim();
      return origin === 'vsm' || source.includes('vsm') || generatedFrom.includes('vsm') || sourceComponentId.length > 0;
    };

    const projectActions = enterpriseActions.filter((action) => action.project_id === planProjectId);
    if (projectActions.length === 0) return;

    const toDeleteIds = new Set<string>();
    const groupedBySourceComponent = new Map<string, EnterpriseAction[]>();
    const groupedByTitle = new Map<string, EnterpriseAction[]>();

    const normalizeTitle = (value: string) =>
      value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ')
        .trim();

    const isMacroSourceId = (id: string) => id.startsWith('vsm_');

    projectActions.forEach((action) => {
      const titleKey = normalizeTitle(action.title ?? '');
      if (titleKey) {
        const titleGroup = groupedByTitle.get(titleKey) ?? [];
        groupedByTitle.set(titleKey, [...titleGroup, action]);
      }
      if (!isVsmGeneratedAction(action)) return;
      const sourceComponentId = String((action.metadata as any)?.sourceComponentId ?? '').trim();
      if (!sourceComponentId) {
        toDeleteIds.add(action.id);
        return;
      }
      const current = groupedBySourceComponent.get(sourceComponentId) ?? [];
      groupedBySourceComponent.set(sourceComponentId, [...current, action]);
    });

    groupedBySourceComponent.forEach((group) => {
      if (group.length <= 1) return;
      const sorted = [...group].sort((left, right) => {
        const statusDiff = statusWeight(right.status) - statusWeight(left.status);
        if (statusDiff !== 0) return statusDiff;
        return new Date(right.updated_at).getTime() - new Date(left.updated_at).getTime();
      });
      const [, ...duplicates] = sorted;
      duplicates.forEach((action) => toDeleteIds.add(action.id));
    });

    groupedByTitle.forEach((group) => {
      if (group.length <= 1) return;
      // Mantém o representante "vencedor": macro > status mais avançado > mais recente.
      const sorted = [...group].sort((left, right) => {
        const leftSrc = String((left.metadata as any)?.sourceComponentId ?? '');
        const rightSrc = String((right.metadata as any)?.sourceComponentId ?? '');
        const statusDiff = statusWeight(right.status) - statusWeight(left.status);
        if (statusDiff !== 0) return statusDiff;
        const macroDiff = Number(isMacroSourceId(rightSrc)) - Number(isMacroSourceId(leftSrc));
        if (macroDiff !== 0) return macroDiff;
        return new Date(right.updated_at).getTime() - new Date(left.updated_at).getTime();
      });
      const [winner, ...losers] = sorted;
      // Só removemos perdedores que sejam VSM-gerados — nunca apaga ação manual do usuário.
      losers.forEach((action) => {
        if (action.id === winner.id) return;
        if (isVsmGeneratedAction(action)) toDeleteIds.add(action.id);
      });
    });

    const idsToDelete = Array.from(toDeleteIds);
    if (idsToDelete.length === 0) return;

    const signature = `${planProjectId}:${idsToDelete.sort().join(',')}`;
    if (autoCleanupSignatureRef.current === signature) return;
    autoCleanupSignatureRef.current = signature;

    void Promise.all(idsToDelete.map((id) => ActionService.delete(id))).then((results) => {
      const removed = results.filter(Boolean).length;
      if (removed <= 0) return;
      showToast('info', `Higienização automática: ${removed} ação(ões) duplicada(s) removida(s).`);
      void queryClient.invalidateQueries({ queryKey: ['enterprise-actions-check', organizationId] });
      // Invalida em todas as chaves de Step5 (qualquer projectId) para manter contadores sincronizados.
      void queryClient.invalidateQueries({ queryKey: ['step5-evidences-actions', organizationId] });
    });
  }, [enterpriseActions, organizationId, planProjectId, queryClient, showToast]);

  const {
    projects,
    selectedProject,
    setSelectedProject,
    setProjects,
    updateProject,
    setCreateActionModalOpen,
  } = useProjectContext();

  // ── Plan project metadata (from local projects list) ──
  const planProject = useMemo(() => {
    if (!isPlanView || !planProjectId) return null;
    return projects.find(p => p.id === planProjectId) ?? null;
  }, [isPlanView, planProjectId, projects]);

  // ── Active plan display name for breadcrumb ──
  const activePlanName = useMemo(() => {
    if (urlPlanId && activePlan) return activePlan.name;
    if (planProjectId) return planProject?.nome_projeto ?? null;
    return null;
  }, [urlPlanId, activePlan, planProjectId, planProject]);

  // ── Team avatars from activePlan metadata ──
  const activePlanTeamAvatars = useMemo(() => {
    const ids: string[] = (activePlan as { metadata?: { team_members?: string[] } } | null)?.metadata?.team_members ?? [];
    return ids.map(id => {
      const m = orgMembers.find(om => om.id === id);
      if (!m) return null;
      const initials = m.name.split(' ').map((w: string) => w[0]).slice(0, 2).join('').toUpperCase();
      return { id, name: m.name, initials };
    }).filter((x): x is { id: string; name: string; initials: string } => x !== null);
  }, [activePlan, orgMembers]);

  // ── Derived plan deadline (latest non-completed action due_date) ──
  const activePlanDueDate = useMemo(() => {
    if (!isActivePlan) return null;
    const dates = enterpriseActions
      .filter(a => a.status !== 'completed' && a.status !== 'cancelled' && a.due_date)
      .map(a => a.due_date!)
      .sort();
    return dates[dates.length - 1] ?? null;
  }, [isActivePlan, enterpriseActions]);

  const {
    currentProjectId,
    setCurrentProjectId,
    getActionsByProject,
    mergeProjectActions,
  } = useProjectStore();

  const hasLocalProjectActions = useMemo(() => {
    if (projects.length === 0) return false;
    if (actionScope === 'all') return projects.some(project => getActionsByProject(project.id).length > 0);
    if (actionScope === 'specific' && specificProjectId) return getActionsByProject(specificProjectId).length > 0;
    if (currentProjectId) return getActionsByProject(currentProjectId).length > 0;
    return false;
  }, [actionScope, currentProjectId, getActionsByProject, projects, specificProjectId]);

  // Convert enterprise actions to local Action format for plan views or enterprise-only mode.
  const enterpriseAsLocalActions = useMemo(() => {
    // In plan view (project-based or standalone), always honor the enterprise result —
    // even when empty — so the list does NOT fall back to local store actions.
    if (isActivePlan) return enterpriseActions.map(enterpriseToAction);
    if (enterpriseActions.length === 0) return undefined;
    // In "all" scope the query runs WITHOUT project/plan filters, returning every org
    // action (including VSM-generated ones that never reach the local store). Always
    // use the enterprise result here so MemoModule shows the full dataset.
    if (actionScope === 'all') return enterpriseActions.map(enterpriseToAction);
    if (projects.length > 0 && hasLocalProjectActions) return undefined;
    return enterpriseActions.map(enterpriseToAction);
  }, [actionScope, enterpriseActions, hasLocalProjectActions, isActivePlan, projects.length]);

  const projectsList = useMemo(() => (
    projects.map((project) => ({
      id: project.id,
      nome_projeto: project.nome_projeto,
      data_criacao: project.data_criacao,
      ultimo_salvamento: project.ultimo_salvamento,
    }))
  ), [projects]);

  const currentProject = useMemo(() => {
    return projects.find((project) => project.id === currentProjectId) ?? selectedProject ?? null;
  }, [projects, currentProjectId, selectedProject]);

  const ribbonActions = useMemo(() => {
    // When enterprise actions are active (no local projects), use them
    if (enterpriseAsLocalActions) return enterpriseAsLocalActions;

    if (actionScope === 'all') {
      return projects.flatMap((project) => getActionsByProject(project.id));
    }

    if (actionScope === 'specific' && specificProjectId) {
      return getActionsByProject(specificProjectId);
    }

    if (!currentProjectId) return [];
    return getActionsByProject(currentProjectId);
  }, [actionScope, currentProjectId, enterpriseAsLocalActions, getActionsByProject, projects, specificProjectId]);

  const ribbonSectors = useMemo(() => (
    Array.from(
      new Set(
        ribbonActions
          .map((action) => action.cardData?.setor)
          .filter((sector): sector is string => Boolean(sector && sector.trim())),
      ),
    ).sort((left, right) => left.localeCompare(right))
  ), [ribbonActions]);

  // ── Status capsules for PageHeader ─────────────────
  const actionStatusItems = useMemo<StatusCapsuleItem[]>(() => {
    let todo = 0, doing = 0, overdue = 0, done = 0;
    const now = Date.now();
    for (const a of ribbonActions) {
      const s = a.cardData?.status ?? a.status;
      if (s === 'Concluído' || s === 'concluido' || s === 'done') done++;
      else if (s === 'Em execução' || s === 'doing' || s === 'em_execucao') doing++;
      else if (s !== 'canceled') todo++;
      // overdue check
      const due = a.cardData?.dataLimite ?? a.deadline ?? a.prazo;
      if (due && s !== 'Concluído' && s !== 'concluido' && s !== 'done' && s !== 'canceled') {
        if (new Date(due).getTime() < now) overdue++;
      }
    }
    return [
      { label: 'A Fazer', value: todo, color: 'slate' },
      { label: 'Em execução', value: doing, color: 'blue' },
      { label: 'Atrasadas', value: overdue, color: 'red' },
      { label: 'Concluídas', value: done, color: 'emerald' },
    ];
  }, [ribbonActions]);

  const ribbonResponsibles = useMemo(() => (
    Array.from(
      new Set(
        ribbonActions
          .map((action) => action.responsavel ?? action.responsible)
          .filter((responsible): responsible is string => Boolean(responsible && responsible.trim())),
      ),
    ).sort((left, right) => left.localeCompare(right))
  ), [ribbonActions]);

  useEffect(() => {
    if (!currentProjectId && projects.length > 0) {
      setCurrentProjectId(projects[0].id);
    }
  }, [currentProjectId, projects, setCurrentProjectId]);

  // ─ Roteamento canônico ─
  // Quando a Central é aberta diretamente pelo menu lateral (sem params),
  // e existe um projeto ativo no contexto, redireciona para a visão
  // drilldown ?project_id=... — mesma experiência da Jornada do Projeto.
  useEffect(() => {
    if (suppressRedirectRef.current) { suppressRedirectRef.current = false; return; }
    if (urlPlanId || planProjectId) return;
    if (actionScope === 'all') return;
    if (!currentProjectId) return;
    if (routeMode === 'new') return;
    navigate(`/acoes?project_id=${currentProjectId}`, { replace: true });
  }, [urlPlanId, planProjectId, actionScope, currentProjectId, navigate, routeMode]);

  useEffect(() => {
    if (currentProject) {
      setSelectedProject(currentProject);
      mergeProjectActions(currentProject.id, currentProject.estado_atual?.actions ?? []);
    }
  }, [currentProject, mergeProjectActions, setSelectedProject]);

  useEffect(() => {
    setCreateActionModalOpen(routeMode === 'new');
  }, [routeMode, setCreateActionModalOpen]);

  useEffect(() => {
    if (isActivePlan) return;
    if (actionScope !== 'specific') return;
    setActionScope('current');
    setSpecificProjectId(null);
  }, [currentProjectId, isActivePlan, actionScope]);

  useEffect(() => {
    const scopedProjectId = planProjectId ?? activePlan?.project_id ?? null;
    if (!isActivePlan || !scopedProjectId) return;
    setActionScope('specific');
    setSpecificProjectId(scopedProjectId);
  }, [activePlan?.project_id, isActivePlan, planProjectId]);

  // ── Enterprise update callback ──
  // Maps local status keys back to enterprise status, optimistically updates cache, persists to Supabase
  const handleEnterpriseUpdateAction = useCallback((actionId: string, data: Partial<Action>) => {
    const dbUpdates: Record<string, unknown> = {};
    const responsibleName = data.responsavel ?? data.responsible;
    const deadline = data.prazo ?? data.deadline;
    const levelToPercent = (level: number | undefined) => {
      if (level == null) return undefined;
      return level <= 1 ? 16 : level === 2 ? 50 : 83;
    };
    const percentToLevel = (value: number | undefined): 1 | 2 | 3 | undefined => {
      if (value == null) return undefined;
      return value < 33 ? 1 : value < 66 ? 2 : 3;
    };

    const effort = 'effort' in data ? data.effort : levelToPercent(data.esforco);
    const impact = 'impact' in data ? data.impact : levelToPercent(data.impacto);
    const effortLevel = 'esforco' in data ? data.esforco as Action['esforco'] : percentToLevel(effort);
    const impactLevel = 'impacto' in data ? data.impacto as Action['impacto'] : percentToLevel(impact);
    const hasMatrixInput = ('esforco' in data || 'effort' in data || 'impacto' in data || 'impact' in data);
    const isMatrixCoordinateOnlyUpdate =
      hasMatrixInput &&
      data.status === undefined &&
      data.title === undefined &&
      data.description === undefined &&
      data.responsibleUserId === undefined &&
      responsibleName === undefined &&
      data.prazo === undefined &&
      data.deadline === undefined;

    const currentMetadata = enterpriseActions.find((action) => action.id === actionId)?.metadata ?? {};
    const nextMetadata: Record<string, unknown> = { ...currentMetadata };

    if (data.status !== undefined) {
      dbUpdates.status = LOCAL_TO_ENTERPRISE_STATUS[data.status] ?? data.status;
    }
    if (data.title !== undefined) dbUpdates.title = data.title;
    if (data.description !== undefined) dbUpdates.description = data.description;
    if (data.responsibleUserId !== undefined) dbUpdates.assigned_to = data.responsibleUserId || null;
    if (responsibleName !== undefined) {
      nextMetadata.assignedName = responsibleName || null;
    }
    if (data.prazo !== undefined || data.deadline !== undefined) dbUpdates.due_date = deadline || null;
    if ('esforco' in data || 'effort' in data) dbUpdates.effort = effort ?? null;
    if ('impacto' in data || 'impact' in data) dbUpdates.impact = impact ?? null;
    if ('esforco' in data || 'effort' in data) nextMetadata.matrixEffort = effort ?? null;
    if ('impacto' in data || 'impact' in data) nextMetadata.matrixImpact = impact ?? null;

    if (Object.keys(nextMetadata).length > 0) {
      dbUpdates.metadata = nextMetadata;
    }

    const localPatch: Partial<Action> = {
      ...data,
      ...(responsibleName !== undefined ? { responsible: responsibleName, responsavel: responsibleName } : {}),
      ...(data.responsibleUserId !== undefined ? { responsibleUserId: data.responsibleUserId } : {}),
      ...(deadline !== undefined ? { deadline, prazo: deadline } : {}),
      ...(effort !== undefined ? { effort } : {}),
      ...(impact !== undefined ? { impact } : {}),
      ...(effortLevel !== undefined ? { esforco: effortLevel } : {}),
      ...(impactLevel !== undefined ? { impacto: impactLevel } : {}),
    };

    const enterpriseAction = enterpriseActions.find((action) => action.id === actionId);
    useProjectStore.setState((state) => {
      const hasAction = state.actions.some((action) => action.id === actionId);
      if (hasAction) {
        return {
          actions: state.actions.map((action) => action.id === actionId
            ? { ...action, ...localPatch, updatedAt: new Date().toISOString() }
            : action),
        };
      }
      if (!enterpriseAction) return state;
      return {
        actions: [...state.actions, { ...enterpriseToAction(enterpriseAction), ...localPatch, updatedAt: new Date().toISOString() }],
      };
    });

    if (Object.keys(dbUpdates).length === 0) return;

    // Optimistic update: patch the react-query cache immediately using the exact key from useQuery
    const cacheKey = ['enterprise-actions', organizationId, actionScope, urlPlanId, planProjectId, dataScope?.type, dataScope?.user_id];
    queryClient.setQueryData<EnterpriseAction[]>(cacheKey, (old) => {
      if (!old) return old;
      return old.map((ea) =>
        ea.id === actionId
          ? {
              ...ea,
              ...dbUpdates,
              assigned_name: responsibleName !== undefined ? responsibleName : ea.assigned_name,
              updated_at: new Date().toISOString(),
            } as EnterpriseAction
          : ea
      );
    });

    // Persist to Supabase; only invalidate when persistence succeeds and when needed.
    void ActionService.update(actionId, dbUpdates as Parameters<typeof ActionService.update>[1]).then((updated) => {
      if (!updated) {
        if (isMatrixCoordinateOnlyUpdate && dbUpdates.metadata) {
          void ActionService.update(actionId, { metadata: dbUpdates.metadata as EnterpriseAction['metadata'] }).then((metadataUpdated) => {
            if (!metadataUpdated) {
              showToast('warning', 'Nao foi possivel salvar a posicao da matriz agora.');
            }
          });
          return;
        }

        showToast('warning', 'Nao foi possivel salvar a posicao da matriz agora.');
        return;
      }

      if (isMatrixCoordinateOnlyUpdate) {
        return;
      }

      void queryClient.invalidateQueries({ queryKey: ['enterprise-actions', organizationId] });
      void queryClient.invalidateQueries({ queryKey: ['step5-evidences-actions', organizationId] });
    });
  }, [actionScope, activePlan?.project_id, dataScope, enterpriseActions, organizationId, planProjectId, queryClient, showToast, urlPlanId]);

  const handleBackToWorkflow = useCallback(() => {
    navigate('/workflow');
  }, [navigate]);

  const handleSelectProject = useCallback((projectId: string) => {
    setCurrentProjectId(projectId);
    const nextProject = projects.find((project) => project.id === projectId) ?? null;
    setSelectedProject(nextProject);
  }, [projects, setCurrentProjectId, setSelectedProject]);

  const handleShowAllProjects = useCallback(() => {
    suppressRedirectRef.current = true;
    setActionScope('all');
    setSpecificProjectId(null);
    if (location.pathname !== '/acoes' || location.search.includes('project_id=')) {
      navigate('/acoes', { replace: true });
    }
    showToast('info', 'Filtro de projeto removido. Exibindo ações de todos os projetos.');
  }, [location.pathname, location.search, navigate, showToast]);

  const handleNavigateToCard = useCallback((cardId: string) => {
    navigate('/mapa', { state: { selectedCardId: cardId, focusOrigin: true } });
  }, [navigate]);

  const handleCreateProject = useCallback(() => {
    navigate('/projetos');
  }, [navigate]);

  const handleOpenNewAction = useCallback(() => {
    setShowPlanNewActionModal(true);
  }, []);

  const handleOpenNewPlan = useCallback(() => {
    setEditingPlan(null);
    setPlanTeamMembers([]);
    setPlanTeamSearch('');
    setShowNewPlanModal(true);
  }, []);

  const handleOpenEditPlan = useCallback(() => {
    if (!activePlan) return;
    setEditingPlan(activePlan);
    const members = (activePlan as { metadata?: { team_members?: string[] } }).metadata?.team_members ?? [];
    setPlanTeamMembers(members);
    setPlanTeamSearch('');
    setShowNewPlanModal(true);
  }, [activePlan]);

  const handleCloseNewPlanModal = useCallback(() => {
    setShowNewPlanModal(false);
    setEditingPlan(null);
    setPlanTeamMembers([]);
    setPlanTeamSearch('');
  }, []);

  const handleNewMap = useCallback(() => {
    navigate('/mapa');
  }, [navigate]);

  const syncProjectActions = useCallback((projectId: string, nextActions: Action[]) => {
    mergeProjectActions(projectId, nextActions);
    const project = projects.find((item) => item.id === projectId);
    if (project) {
      updateProject(projectId, {
        estado_atual: {
          ...project.estado_atual,
          actions: nextActions,
        },
        ultimo_salvamento: new Date().toISOString(),
      });
    }
  }, [mergeProjectActions, projects, updateProject]);

  const handleSaveLocal = useCallback(async () => {
    if (!currentProject) {
      showToast('warning', 'Nenhum projeto selecionado para salvar.');
      return;
    }

    try {
      const projectActions = getActionsByProject(currentProject.id);
      const mapState = useProjectStore.getState();
      const activeSheetId = mapState.activeSheetId ?? mapState.sheets[0]?.id ?? generateId();
      const currentSheets = mapState.currentProjectId === currentProject.id
        ? (mapState.sheets.length
            ? mapState.sheets
            : [{ id: activeSheetId, name: 'MAPA 1', cards: [] }])
        : (currentProject.estado_atual?.sheets?.length
            ? currentProject.estado_atual.sheets
            : [{ id: currentProject.estado_atual?.activeSheetId ?? activeSheetId, name: 'MAPA 1', cards: currentProject.estado_atual?.cards ?? [] }]);
      const projectToSave: Project = {
        ...currentProject,
        estado_atual: {
          ...(currentProject.estado_atual ?? {}),
          sheets: currentSheets,
          activeSheetId: currentProject.estado_atual?.activeSheetId ?? currentSheets[0]?.id ?? activeSheetId,
          cards: currentProject.estado_atual?.cards ?? currentSheets.find(sheet => sheet.id === (currentProject.estado_atual?.activeSheetId ?? currentSheets[0]?.id))?.cards ?? [],
          mapType: currentProject.estado_atual?.mapType ?? mapState.mapType,
          globalDemand: currentProject.estado_atual?.globalDemand ?? mapState.globalDemand,
          workTime: currentProject.estado_atual?.workTime ?? mapState.workTime,
          taktTime: currentProject.estado_atual?.taktTime ?? mapState.taktTime,
          actions: projectActions,
          postIts: currentProject.estado_atual?.postIts ?? (mapState.currentProjectId === currentProject.id ? mapState.postIts : []),
        },
        ultimo_salvamento: new Date().toISOString(),
      };

      await saveProjectToDisk(projectToSave);
      const nextProjects = projects.some(project => project.id === projectToSave.id)
        ? projects.map(project => project.id === projectToSave.id ? projectToSave : project)
        : [...projects, projectToSave];
      setProjects(nextProjects);
      setSelectedProject(projectToSave);
      try {
        localStorage.setItem('lean_workflow_projects', JSON.stringify(nextProjects));
      } catch {
        // localStorage pode falhar em ambientes restritos; o .wf já foi gerado.
      }
      updateProject(projectToSave.id, {
        estado_atual: projectToSave.estado_atual,
        ultimo_salvamento: projectToSave.ultimo_salvamento,
      });
      showToast('success', 'Projeto salvo localmente.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Falha ao salvar projeto local.';
      showToast('error', message);
    }
  }, [currentProject, getActionsByProject, projects, setProjects, setSelectedProject, showToast, updateProject]);

  const handleOpenLocal = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const project = await loadProjectFromDisk(file);
      const existingIndex = projects.findIndex((item) => item.id === project.id);
      const nextProjects = existingIndex >= 0
        ? projects.map((item) => (item.id === project.id ? project : item))
        : [...projects, project];

      setProjects(nextProjects);
      persistProjects(nextProjects);
      setSelectedProject(project);
      setCurrentProjectId(project.id);
      mergeProjectActions(project.id, project.estado_atual?.actions ?? []);
      setCreateActionModalOpen(false);
      showToast('success', `Projeto carregado: ${project.nome_projeto}`);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Falha ao abrir arquivo local.';
      showToast('error', message);
    } finally {
      event.target.value = '';
    }
  }, [mergeProjectActions, projects, setCreateActionModalOpen, setCurrentProjectId, setProjects, setSelectedProject, showToast]);

  const handleImportExcel = useCallback(() => {
    if (!currentProjectId) {
      showToast('warning', 'Selecione um projeto antes de importar Excel.');
      return;
    }

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx,.xls';
    input.onchange = async (changeEvent) => {
      const file = (changeEvent.target as HTMLInputElement).files?.[0];
      if (!file) return;

      try {
        const importedActions = await importActionsFromExcel(file, currentProjectId);
        if (importedActions.length === 0) {
          showToast('warning', 'Nenhuma ação válida encontrada na planilha.');
          return;
        }

        const currentActions = getActionsByProject(currentProjectId);
        const mergedActions = [...currentActions, ...importedActions];
        syncProjectActions(currentProjectId, mergedActions);
        showToast('success', `${importedActions.length} ações importadas do Excel.`);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Falha ao importar planilha.';
        showToast('error', message);
      }
    };
    input.click();
  }, [currentProjectId, getActionsByProject, showToast, syncProjectActions]);

  const handleDownloadTemplate = useCallback(async () => {
    try {
      await downloadTemplate();
      showToast('success', 'Template de Excel gerado.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Falha ao baixar template.';
      showToast('error', message);
    }
  }, [showToast]);

  const handleExportExcel = useCallback(() => {
    if (!currentProject) {
      showToast('warning', 'Nenhum projeto selecionado para exportar.');
      return;
    }

    void (async () => {
      try {
        const XLSX = await import('xlsx');
        const projectActions = getActionsByProject(currentProject.id);
        if (projectActions.length === 0) {
          showToast('warning', 'Nenhuma acao disponivel para exportacao.');
          return;
        }

        const rows = projectActions.map((action) => ({
          Titulo: action.title ?? '',
          Descricao: action.description ?? '',
          Status: action.status ?? 'todo',
          Responsavel: action.responsavel ?? action.responsible ?? '',
          Prazo: action.prazo ?? action.deadline ?? '',
          Impacto: action.impacto ?? action.priority?.impacto ?? '',
          Esforco: action.esforco ?? action.priority?.esforco ?? '',
        }));

        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Acoes');
        XLSX.writeFile(workbook, `${currentProject.nome_projeto || 'acoes'}.xlsx`);
        showToast('success', 'Excel exportado com sucesso.');
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Falha ao exportar Excel.';
        showToast('error', message);
      }
    })();
  }, [currentProject, getActionsByProject, showToast]);

  const handleExportPDF = useCallback(async (type?: 'list' | 'kanban' | 'matrix' | 'indicators' | 'dashboard') => {
    const element = exportRootRef.current;
    if (!element) {
      showToast('warning', 'Área de exportação indisponível.');
      return;
    }

    try {
      const projectName = currentProject?.nome_projeto || 'acoes';
      await exportService.toPDF(element, type ? `${projectName}-${type}` : projectName);
      showToast('success', 'PDF exportado com sucesso.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Falha ao exportar PDF.';
      showToast('error', message);
    }
  }, [currentProject?.nome_projeto, showToast]);

  const handleExportImage = useCallback(async (type?: 'list' | 'kanban' | 'matrix' | 'indicators' | 'dashboard') => {
    const element = exportRootRef.current;
    if (!element) {
      showToast('warning', 'Área de exportação indisponível.');
      return;
    }

    try {
      const projectName = currentProject?.nome_projeto || 'acoes';
      await exportService.toImage(element, type ? `${projectName}-${type}` : projectName);
      showToast('success', 'PNG exportado com sucesso.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Falha ao exportar PNG.';
      showToast('error', message);
    }
  }, [currentProject?.nome_projeto, showToast]);

  // ── Determine active view from route ────────────
  const activeView: AcoesViewMode = useMemo(() => {
    if (location.pathname.startsWith('/acoes/kanban')) return 'kanban';
    if (location.pathname.startsWith('/acoes/matriz')) return 'matrix';
    if (location.pathname.startsWith('/acoes/dashboard')) return 'dashboard';
    return 'list';
  }, [location.pathname]);

  const buildActionRoute = useCallback((path: string) => `${path}${location.search}`, [location.search]);

  // ── Filters widget (portal dropdowns like Problemas) ────
  type FilterDd = 'projeto' | 'status' | 'setor' | null;
  const [openFilterDd, setOpenFilterDd] = useState<FilterDd>(null);
  const projetoBtnRef = useRef<HTMLButtonElement>(null);
  const statusBtnRef = useRef<HTMLButtonElement>(null);
  const setorBtnRef = useRef<HTMLButtonElement>(null);
  const [filterDdPos, setFilterDdPos] = useState({ top: 0, left: 0 });

  const toggleFilterDd = useCallback((name: FilterDd) => {
    setOpenFilterDd(prev => {
      const next = prev === name ? null : name;
      if (next) {
        const ref = name === 'projeto' ? projetoBtnRef : name === 'status' ? statusBtnRef : setorBtnRef;
        const rect = ref.current?.getBoundingClientRect();
        if (rect) setFilterDdPos({ top: rect.bottom + 4, left: rect.left });
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!openFilterDd) return;
    const handle = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest('[data-acoes-filter-dd]')) return;
      setOpenFilterDd(null);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [openFilterDd]);

  const filterBtnClass = 'flex h-[30px] shrink-0 items-center gap-1.5 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 text-[12px] text-[var(--text-primary)] transition-colors hover:bg-[var(--button-hover)]';
  const ddPanelClass = 'ribbon-dropdown-panel absolute z-[9999] min-w-[160px] overflow-hidden rounded-[5px] border border-[var(--border-default)] bg-[var(--bg-surface)] py-1 shadow-2xl';
  const ddItemClass = 'flex w-full items-center gap-2 px-3 py-[6px] text-left text-[12px] text-[var(--text-primary)] transition-colors hover:bg-[var(--button-hover)]';
  const ddSectionClass = 'px-2 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--text-secondary)]';

  const activeProjetoLabel = actionScope === 'current' ? 'Atual' : actionScope === 'all' ? 'Todos' : (projectsList.find(p => p.id === specificProjectId)?.nome_projeto ?? 'Projeto');
  const activeStatusLabel = selectedStatus === 'all' ? 'Todos' : selectedStatus === 'todo' ? 'A Fazer' : selectedStatus === 'doing' ? 'Em Andamento' : selectedStatus === 'done' ? 'Concluído' : 'Cancelada';
  const activeSetorLabel = selectedSector === 'all' ? 'Todos' : selectedSector;
  const scopedProjectName = actionScope === 'specific'
    ? projectsList.find(p => p.id === specificProjectId)?.nome_projeto ?? 'Projeto'
    : (currentProject?.nome_projeto ?? selectedProject?.nome_projeto ?? 'Projeto atual');

  const filtersWidget = useMemo(() => (
    // In plan view, the Projeto button becomes a plan-aware switcher + "Ver todos";
    // remaining filters (Status, Setor, Search) follow the standard layout.
    isActivePlan ? (
    <div className="flex items-center gap-1.5">
      {/* Projeto/Plano switcher — drilldown mode */}
      <button ref={projetoBtnRef} data-acoes-filter-dd="" type="button" className={filterBtnClass} onClick={() => toggleFilterDd('projeto')}>
        <Folder size={12} className="text-[var(--text-secondary)]" />
        <span className="truncate max-w-[140px]">{activePlanName ?? 'Projeto'}</span>
        <ChevronDown size={10} className="text-[var(--text-secondary)]" />
      </button>
      <button
        type="button"
        onClick={handleShowAllProjects}
        className="flex h-[30px] shrink-0 items-center gap-1.5 rounded-[3px] border border-blue-300/50 bg-blue-500/10 px-2 text-[11px] text-blue-700 transition-colors hover:bg-blue-500/15 dark:border-blue-500/30 dark:text-blue-300"
        title="Remover filtro de projeto/plano e exibir todas as ações"
      >
        <Folder size={12} />
        <span className="opacity-90">Ver todos</span>
      </button>
      {openFilterDd === 'projeto' && createPortal(
        <div data-acoes-filter-dd="" className={ddPanelClass} style={{ position: 'fixed', top: filterDdPos.top, left: filterDdPos.left }}>
          <button type="button" className={ddItemClass} onClick={() => { setOpenFilterDd(null); navigate('/acoes'); }}>
            <span className="inline-block h-[7px] w-[7px] shrink-0" />
            <span className="flex-1">Todos os projetos</span>
          </button>
          {projectsList.length > 0 && (
            <>
              <div className={ddSectionClass}>Projetos</div>
              {projectsList.map(project => (
                <button key={project.id} type="button" className={ddItemClass} onClick={() => { setOpenFilterDd(null); navigate(`/acoes?project_id=${project.id}`); }}>
                  <span className="inline-block h-[7px] w-[7px] shrink-0 rounded-full bg-blue-500" />
                  <span className="flex-1 truncate">{project.nome_projeto}</span>
                  <Check size={11} className={planProjectId === project.id ? 'opacity-100' : 'opacity-0'} />
                </button>
              ))}
            </>
          )}
          {projectsList.length === 0 && enterpriseProjects.length > 0 && (
            <>
              <div className={ddSectionClass}>Projetos</div>
              {enterpriseProjects.map(project => (
                <button key={project.id} type="button" className={ddItemClass} onClick={() => { setOpenFilterDd(null); navigate(`/acoes?project_id=${project.id}`); }}>
                  <span className="inline-block h-[7px] w-[7px] shrink-0 rounded-full bg-blue-500" />
                  <span className="flex-1 truncate">{project.name}</span>
                  <Check size={11} className={planProjectId === project.id ? 'opacity-100' : 'opacity-0'} />
                </button>
              ))}
            </>
          )}
          {standalonePlans.length > 0 && (
            <>
              <div className={ddSectionClass}>Planos Avulsos</div>
              {standalonePlans.map(plan => (
                <button key={plan.id} type="button" className={ddItemClass} onClick={() => { setOpenFilterDd(null); navigate(`/acoes?planId=${plan.id}`); }}>
                  <span className="inline-block h-[7px] w-[7px] shrink-0 rounded-sm bg-amber-500" />
                  <span className="flex-1 truncate">{plan.name}</span>
                  <Check size={11} className={urlPlanId === plan.id ? 'opacity-100' : 'opacity-0'} />
                </button>
              ))}
            </>
          )}
        </div>,
        document.body
      )}
      {/* Status */}
      <button ref={statusBtnRef} data-acoes-filter-dd="" type="button" className={filterBtnClass} onClick={() => toggleFilterDd('status')}>
        <ListTodo size={12} className="text-[var(--text-secondary)]" />
        <span className="truncate">{activeStatusLabel}</span>
        <ChevronDown size={10} className="text-[var(--text-secondary)]" />
      </button>
      {openFilterDd === 'status' && createPortal(
        <div data-acoes-filter-dd="" className={ddPanelClass} style={{ position: 'fixed', top: filterDdPos.top, left: filterDdPos.left }}>
          {[
            { value: 'all', label: 'Todos', color: '' },
            { value: 'todo', label: 'A Fazer', color: '#94a3b8' },
            { value: 'doing', label: 'Em Andamento', color: '#3b82f6' },
            { value: 'done', label: 'Concluído', color: '#22c55e' },
            { value: 'canceled', label: 'Cancelada', color: '#ef4444' },
          ].map(item => (
            <button key={item.value} type="button" className={ddItemClass} onClick={() => { setSelectedStatus(item.value); setOpenFilterDd(null); }}>
              {item.color ? <span className="inline-block h-[7px] w-[7px] shrink-0 rounded-full" style={{ backgroundColor: item.color }} /> : <span className="inline-block h-[7px] w-[7px] shrink-0" />}
              <span className="flex-1">{item.label}</span>
              <Check size={11} className={selectedStatus === item.value ? 'opacity-100' : 'opacity-0'} />
            </button>
          ))}
        </div>,
        document.body
      )}

      {/* Setor */}
      <button ref={setorBtnRef} data-acoes-filter-dd="" type="button" className={filterBtnClass} onClick={() => toggleFilterDd('setor')}>
        <Factory size={12} className="text-[var(--text-secondary)]" />
        <span className="truncate">{activeSetorLabel}</span>
        <ChevronDown size={10} className="text-[var(--text-secondary)]" />
      </button>
      {openFilterDd === 'setor' && createPortal(
        <div data-acoes-filter-dd="" className={ddPanelClass} style={{ position: 'fixed', top: filterDdPos.top, left: filterDdPos.left }}>
          <button type="button" className={ddItemClass} onClick={() => { setSelectedSector('all'); setOpenFilterDd(null); }}>
            <span className="inline-block h-[7px] w-[7px] shrink-0" />
            <span className="flex-1">Todos</span>
            <Check size={11} className={selectedSector === 'all' ? 'opacity-100' : 'opacity-0'} />
          </button>
          {ribbonSectors.map(sector => (
            <button key={sector} type="button" className={ddItemClass} onClick={() => { setSelectedSector(sector); setOpenFilterDd(null); }}>
              <span className="inline-block h-[7px] w-[7px] shrink-0" />
              <span className="flex-1">{sector}</span>
              <Check size={11} className={selectedSector === sector ? 'opacity-100' : 'opacity-0'} />
            </button>
          ))}
        </div>,
        document.body
      )}

      {/* Search */}
      <div className="flex w-[220px] shrink-0 items-center">
        <div className="flex w-full items-center gap-2 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2">
          <Search size={14} className="shrink-0 text-[var(--text-secondary)]" />
          <input
            type="text"
            placeholder="Buscar ação, responsável..."
            value={ribbonSearchText}
            onChange={e => setRibbonSearchText(e.target.value)}
            className={`w-full ${desktopRibbonInputClass} border-0 bg-transparent px-0`}
          />
          {ribbonSearchText && (
            <button onClick={() => setRibbonSearchText('')} className="shrink-0 rounded p-0.5 hover:bg-[var(--button-hover)]">
              <X size={10} className="text-[var(--text-secondary)]" />
            </button>
          )}
        </div>
      </div>
    </div>
    ) : (
    <div className="flex items-center gap-1.5">
      {(actionScope === 'specific' || actionScope === 'current') && (
        <button
          type="button"
          onClick={handleShowAllProjects}
          className="flex h-[30px] shrink-0 items-center gap-1.5 rounded-[3px] border border-blue-300/50 bg-blue-500/10 px-2 text-[11px] text-blue-700 transition-colors hover:bg-blue-500/15 dark:border-blue-500/30 dark:text-blue-300"
          title={`Projeto ativo: ${scopedProjectName}. Clique para ver todos.`}
        >
          <Folder size={12} />
          <span className="max-w-[140px] truncate">{scopedProjectName}</span>
          <span className="opacity-80">• Ver todos</span>
        </button>
      )}

      {/* Projeto */}
      <button ref={projetoBtnRef} data-acoes-filter-dd="" type="button" className={filterBtnClass} onClick={() => toggleFilterDd('projeto')}>
        <Folder size={12} className="text-[var(--text-secondary)]" />
        <span className="truncate">{activeProjetoLabel}</span>
        <ChevronDown size={10} className="text-[var(--text-secondary)]" />
      </button>
      {openFilterDd === 'projeto' && createPortal(
        <div data-acoes-filter-dd="" className={ddPanelClass} style={{ position: 'fixed', top: filterDdPos.top, left: filterDdPos.left }}>
          {[{ value: 'current', label: 'Atual' }, { value: 'all', label: 'Todos' }].map(item => (
            <button key={item.value} type="button" className={ddItemClass} onClick={() => { setActionScope(item.value as 'current' | 'all'); setSpecificProjectId(null); setOpenFilterDd(null); }}>
              <span className="inline-block h-[7px] w-[7px] shrink-0" />
              <span className="flex-1">{item.label}</span>
              <Check size={11} className={actionScope === item.value && !specificProjectId ? 'opacity-100' : 'opacity-0'} />
            </button>
          ))}
          {projectsList.length > 0 && (
            <>
              <div className={ddSectionClass}>Específico</div>
              {projectsList.map(project => (
                <div key={project.id} className="flex items-center">
                  <button type="button" className={`${ddItemClass} flex-1`} onClick={() => { setActionScope('specific'); setSpecificProjectId(project.id); setOpenFilterDd(null); }}>
                    <span className="inline-block h-[7px] w-[7px] shrink-0" />
                    <span className="flex-1">{project.nome_projeto}</span>
                    <Check size={11} className={specificProjectId === project.id ? 'opacity-100' : 'opacity-0'} />
                  </button>
                  <button
                    type="button"
                    title="Abrir como Plano de Ação"
                    className="shrink-0 px-2 py-[6px] text-[var(--text-muted)] hover:text-blue-500 transition-colors"
                    onClick={() => { setOpenFilterDd(null); navigate(`/acoes?project_id=${project.id}`); }}
                  >
                    <MapIcon size={11} />
                  </button>
                </div>
              ))}
            </>
          )}
          {projectsList.length === 0 && enterpriseProjects.length > 0 && (
            <>
              <div className={ddSectionClass}>Projetos</div>
              {enterpriseProjects.map(project => (
                <div key={project.id} className="flex items-center">
                  <button type="button" className={`${ddItemClass} flex-1`} onClick={() => { setOpenFilterDd(null); navigate(`/acoes?project_id=${project.id}`); }}>
                    <span className="inline-block h-[7px] w-[7px] shrink-0 rounded-full bg-blue-500" />
                    <span className="flex-1 truncate">{project.name}</span>
                  </button>
                  <button
                    type="button"
                    title="Abrir como Plano de Ação"
                    className="shrink-0 px-2 py-[6px] text-[var(--text-muted)] hover:text-blue-500 transition-colors"
                    onClick={() => { setOpenFilterDd(null); navigate(`/acoes?project_id=${project.id}`); }}
                  >
                    <MapIcon size={11} />
                  </button>
                </div>
              ))}
            </>
          )}
          {standalonePlans.length > 0 && (
            <>
              <div className={ddSectionClass}>Planos Avulsos</div>
              {standalonePlans.map(plan => (
                <button
                  key={plan.id}
                  type="button"
                  className={ddItemClass}
                  onClick={() => { setOpenFilterDd(null); navigate(`/acoes?planId=${plan.id}`); }}
                >
                  <span className="inline-block h-[7px] w-[7px] shrink-0 rounded-sm bg-amber-500" />
                  <span className="flex-1 truncate">{plan.name}</span>
                  {urlPlanId === plan.id && <Check size={11} className="shrink-0 text-blue-500" />}
                </button>
              ))}
            </>
          )}
        </div>,
        document.body
      )}

      {/* Status */}
      <button ref={statusBtnRef} data-acoes-filter-dd="" type="button" className={filterBtnClass} onClick={() => toggleFilterDd('status')}>
        <ListTodo size={12} className="text-[var(--text-secondary)]" />
        <span className="truncate">{activeStatusLabel}</span>
        <ChevronDown size={10} className="text-[var(--text-secondary)]" />
      </button>
      {openFilterDd === 'status' && createPortal(
        <div data-acoes-filter-dd="" className={ddPanelClass} style={{ position: 'fixed', top: filterDdPos.top, left: filterDdPos.left }}>
          {[
            { value: 'all', label: 'Todos', color: '' },
            { value: 'todo', label: 'A Fazer', color: '#94a3b8' },
            { value: 'doing', label: 'Em Andamento', color: '#3b82f6' },
            { value: 'done', label: 'Concluído', color: '#22c55e' },
            { value: 'canceled', label: 'Cancelada', color: '#ef4444' },
          ].map(item => (
            <button key={item.value} type="button" className={ddItemClass} onClick={() => { setSelectedStatus(item.value); setOpenFilterDd(null); }}>
              {item.color ? <span className="inline-block h-[7px] w-[7px] shrink-0 rounded-full" style={{ backgroundColor: item.color }} /> : <span className="inline-block h-[7px] w-[7px] shrink-0" />}
              <span className="flex-1">{item.label}</span>
              <Check size={11} className={selectedStatus === item.value ? 'opacity-100' : 'opacity-0'} />
            </button>
          ))}
        </div>,
        document.body
      )}

      {/* Setor */}
      <button ref={setorBtnRef} data-acoes-filter-dd="" type="button" className={filterBtnClass} onClick={() => toggleFilterDd('setor')}>
        <Factory size={12} className="text-[var(--text-secondary)]" />
        <span className="truncate">{activeSetorLabel}</span>
        <ChevronDown size={10} className="text-[var(--text-secondary)]" />
      </button>
      {openFilterDd === 'setor' && createPortal(
        <div data-acoes-filter-dd="" className={ddPanelClass} style={{ position: 'fixed', top: filterDdPos.top, left: filterDdPos.left }}>
          <button type="button" className={ddItemClass} onClick={() => { setSelectedSector('all'); setOpenFilterDd(null); }}>
            <span className="inline-block h-[7px] w-[7px] shrink-0" />
            <span className="flex-1">Todos</span>
            <Check size={11} className={selectedSector === 'all' ? 'opacity-100' : 'opacity-0'} />
          </button>
          {ribbonSectors.map(sector => (
            <button key={sector} type="button" className={ddItemClass} onClick={() => { setSelectedSector(sector); setOpenFilterDd(null); }}>
              <span className="inline-block h-[7px] w-[7px] shrink-0" />
              <span className="flex-1">{sector}</span>
              <Check size={11} className={selectedSector === sector ? 'opacity-100' : 'opacity-0'} />
            </button>
          ))}
        </div>,
        document.body
      )}

      {/* Search */}
      <div className="flex w-[220px] shrink-0 items-center">
        <div className="flex w-full items-center gap-2 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2">
          <Search size={14} className="shrink-0 text-[var(--text-secondary)]" />
          <input
            type="text"
            placeholder="Buscar ação, responsável..."
            value={ribbonSearchText}
            onChange={e => setRibbonSearchText(e.target.value)}
            className={`w-full ${desktopRibbonInputClass} border-0 bg-transparent px-0`}
          />
          {ribbonSearchText && (
            <button onClick={() => setRibbonSearchText('')} className="shrink-0 rounded p-0.5 hover:bg-[var(--button-hover)]">
              <X size={10} className="text-[var(--text-secondary)]" />
            </button>
          )}
        </div>
      </div>
    </div>
  )), [isActivePlan, actionScope, specificProjectId, selectedStatus, selectedSector, ribbonSearchText, openFilterDd, filterDdPos, activeProjetoLabel, activeStatusLabel, activeSetorLabel, scopedProjectName, projectsList, ribbonSectors, standalonePlans, toggleFilterDd, activePlanName, planProjectId, urlPlanId, enterpriseProjects, navigate, handleShowAllProjects, currentProject?.nome_projeto, selectedProject?.nome_projeto]);

  // ── Topbar header meta: breadcrumb when plan is active ──
  const acoeHeaderMeta = React.useMemo(() => {
    if (!isActivePlan || !activePlanName) return null;
    return (
      <button
        type="button"
        onClick={handleShowAllProjects}
        title="Ver todas as ações"
        className="max-w-[220px] truncate text-[12px] font-medium text-[var(--text-primary)] hover:underline hover:underline-offset-2"
      >
        {activePlanName}
      </button>
    );
  }, [isActivePlan, activePlanName, handleShowAllProjects]);

  const ribbonConfig = useAcoesRibbon({
    onNewAction: handleOpenNewAction,
    onNewPlan: handleOpenNewPlan,
    onEditPlan: handleOpenEditPlan,
    onOpenLocal: handleOpenLocal,
    onSaveLocal: handleSaveLocal,
    onSaveAs: handleSaveLocal,
    onImportExcel: handleImportExcel,
    onDownloadTemplate: handleDownloadTemplate,
    onExportPDF: handleExportPDF,
    onExportImage: handleExportImage,
    activeView,
    onNavigateList: () => navigate(buildActionRoute('/acoes')),
    onNavigateKanban: () => navigate(buildActionRoute('/acoes/kanban')),
    onNavigateMatrix: () => navigate(buildActionRoute('/acoes/matriz')),
    onNavigateDashboard: () => navigate(buildActionRoute('/acoes/dashboard')),
    kanbanDensity,
    onKanbanDensityChange: setKanbanDensity,
    filtersWidget: filtersWidget,
    isPlanSelected: isActivePlan || !!currentProjectId || !!enterpriseAsLocalActions,
    canEditPlan: !!activePlan,
  });

  return (
    <ModuleLayout ribbonConfig={ribbonConfig} headerMeta={acoeHeaderMeta}>
      <div className="page-container flex h-full w-full flex-col overflow-hidden dark:bg-black">
        <input
          ref={fileInputRef}
          type="file"
          accept=".wf"
          className="hidden"
          onChange={handleFileInputChange}
        />

        {/* ── Plan View Header — single compact line ──────── */}
        {isActivePlan && planKpis && (
          <div className={`flex shrink-0 items-center gap-3 overflow-hidden border-b border-[var(--border-default)] bg-[var(--bg-surface)] px-4 py-1.5 ${
            planKpis.status === 'critical' ? 'border-l-[3px] border-l-red-500'
            : planKpis.status === 'risk'    ? 'border-l-[3px] border-l-amber-500'
            :                                 'border-l-[3px] border-l-emerald-500'
          }`}>

            {/* Breadcrumb — "Central de Ações" clicável */}
            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                onClick={handleShowAllProjects}
                title="Ver todas as ações"
                className="text-[10px] font-semibold uppercase tracking-widest text-[var(--text-muted)] transition-colors hover:text-[var(--text-secondary)]"
              >
                Central de Ações
              </button>
              <ChevronRight size={10} className="shrink-0 text-[var(--text-muted)]" />
              <span className="max-w-[180px] truncate text-[12px] font-semibold text-[var(--text-primary)]">
                {activePlanName ?? 'Plano sem título'}
              </span>
              <span className={`ml-1 shrink-0 inline-flex items-center gap-1 rounded-[3px] px-1.5 py-0.5 text-[9px] font-bold uppercase ${
                planKpis.status === 'critical' ? 'bg-red-500/15 text-red-500'
                : planKpis.status === 'risk'   ? 'bg-amber-500/15 text-amber-500'
                :                               'bg-emerald-500/15 text-emerald-500'
              }`}>
                {planKpis.status === 'critical' ? <><AlertTriangle size={8} /> CRÍTICO</>
                  : planKpis.status === 'risk'  ? <><TrendingUp size={8} /> EM RISCO</>
                  :                              <><CheckCircle2 size={8} /> EM DIA</>}
              </span>
            </div>

            <div className="h-3.5 w-px shrink-0 bg-[var(--border-default)]" />

            {/* KPIs strip */}
            <div className="flex min-w-0 flex-1 items-center gap-2.5 overflow-hidden text-[11px]">
              {/* Progresso mini-bar */}
              <div className="flex shrink-0 items-center gap-1.5">
                <div className="h-[5px] w-[44px] overflow-hidden rounded-full bg-[var(--bg-app)]">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      planKpis.progress >= 80 ? 'bg-emerald-500'
                      : planKpis.progress >= 50 ? 'bg-amber-500'
                      : 'bg-red-500'
                    }`}
                    style={{ width: `${planKpis.progress}%` }}
                  />
                </div>
                <span className={`font-semibold tabular-nums ${
                  planKpis.progress >= 80 ? 'text-emerald-500'
                  : planKpis.progress >= 50 ? 'text-amber-500'
                  : 'text-red-500'
                }`}>{planKpis.progress}%</span>
              </div>

              <div className="h-3 w-px shrink-0 bg-[var(--border-default)]" />

              {/* Prazo */}
              <div className="flex shrink-0 items-center gap-1">
                <Clock size={10} className="shrink-0 text-[var(--text-muted)]" />
                <span className={`tabular-nums ${
                  activePlanDueDate && new Date(activePlanDueDate).getTime() < Date.now()
                    ? 'font-semibold text-red-500'
                    : 'text-[var(--text-secondary)]'
                }`}>
                  {activePlanDueDate
                    ? new Date(activePlanDueDate).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
                    : '—'}
                </span>
              </div>

              <div className="h-3 w-px shrink-0 bg-[var(--border-default)]" />

              {/* Total · Concluídas · Atrasadas */}
              {([
                { label: 'Total',      value: planKpis.total,   color: 'text-[var(--text-secondary)]' },
                { label: 'Concluídas', value: planKpis.done,    color: 'text-emerald-500' },
                { label: 'Atrasadas',  value: planKpis.overdue, color: planKpis.overdue > 0 ? 'text-red-500' : 'text-[var(--text-muted)]' },
              ] as const).map(({ label, value, color }, i) => (
                <React.Fragment key={label}>
                  {i > 0 && <div className="h-3 w-px shrink-0 bg-[var(--border-default)]" />}
                  <div className="flex shrink-0 items-center gap-1">
                    <span className="text-[var(--text-muted)]">{label}</span>
                    <span className={`font-semibold tabular-nums ${color}`}>{value}</span>
                  </div>
                </React.Fragment>
              ))}

              {/* Equipe avatars */}
              {activePlanTeamAvatars.length > 0 && (
                <>
                  <div className="h-3 w-px shrink-0 bg-[var(--border-default)]" />
                  <div className="flex shrink-0 -space-x-1">
                    {activePlanTeamAvatars.slice(0, 5).map(m => (
                      <div
                        key={m.id}
                        title={m.name}
                        className="flex h-[16px] w-[16px] shrink-0 items-center justify-center rounded-sm border border-[var(--bg-surface)] bg-[var(--bg-elevated)] text-[7px] font-bold text-[var(--text-secondary)]"
                      >
                        {m.initials}
                      </div>
                    ))}
                    {activePlanTeamAvatars.length > 5 && (
                      <div className="flex h-[16px] w-[16px] items-center justify-center rounded-sm border border-[var(--bg-surface)] bg-[var(--bg-elevated)] text-[7px] text-[var(--text-muted)]">
                        +{activePlanTeamAvatars.length - 5}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* CTAs */}
            <div className="flex shrink-0 items-center gap-1">
              {planProjectId && planProjectMapId && (
                <button
                  type="button"
                  onClick={() => navigate(`/mapa?project_id=${planProjectId}`)}
                  className="flex items-center gap-1 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-elevated)] px-2 py-0.5 text-[11px] font-medium text-[var(--text-secondary)] transition-colors hover:bg-[var(--button-hover)]"
                >
                  <MapIcon size={10} /> Mapa
                </button>
              )}
              {planProjectId && (
                <button
                  type="button"
                  onClick={() => navigate(`/execucoes?project_id=${planProjectId}`)}
                  className="flex items-center gap-1 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-elevated)] px-2 py-0.5 text-[11px] font-medium text-[var(--text-secondary)] transition-colors hover:bg-[var(--button-hover)]"
                >
                  <Workflow size={10} /> Execuções
                </button>
              )}
              <button
                type="button"
                onClick={handleOpenNewAction}
                className="flex items-center gap-1 rounded-[3px] bg-blue-600 px-2.5 py-0.5 text-[11px] font-semibold text-white transition-colors hover:bg-blue-700"
              >
                <Plus size={11} /> Ação
              </button>
              <button
                type="button"
                onClick={handleShowAllProjects}
                title="Fechar plano — ver todas as ações"
                className="rounded-sm p-1 text-[var(--text-muted)] transition-colors hover:bg-[var(--button-hover)] hover:text-[var(--text-primary)]"
              >
                <X size={13} />
              </button>
            </div>
          </div>
        )}

        <div ref={exportRootRef} className="min-h-0 flex-1">
          <MemoModule
            projects={projectsList}
            currentProjectId={currentProjectId}
            onBackToWorkflow={handleBackToWorkflow}
            onSelectProject={handleSelectProject}
            zoom={kanbanDensity === '3col' ? 0.5 : kanbanDensity === '2col' ? 0.7 : 1}
            onClose={() => navigate('/centro-operacional')}
            onNavigateToCard={handleNavigateToCard}
            initialActionId={null}
            initialProjectId={currentProjectId}
            onCreateProject={handleCreateProject}
            actionRibbonSearchText={ribbonSearchText}
            onActionRibbonSearchChange={setRibbonSearchText}
            initialView={routeMode === 'kanban' ? 'kanban' : routeMode === 'matrix' ? 'matrix' : 'list'}
            initialIndicatorsOpen={routeMode === 'dashboard'}
            onImportExcel={handleImportExcel}
            onDownloadTemplate={handleDownloadTemplate}
            onSaveLocal={handleSaveLocal}
            onOpenLocal={handleOpenLocal}
            onExportPDF={handleExportPDF}
            onExportImage={handleExportImage}
            onNewMap={handleNewMap}
            showRibbon={false}
            selectedSector={selectedSector}
            onSelectedSectorChange={setSelectedSector}
            selectedResponsible={selectedResponsible}
            onSelectedResponsibleChange={setSelectedResponsible}
            selectedStatus={selectedStatus}
            onSelectedStatusChange={setSelectedStatus}
            actionScope={actionScope}
            onActionScopeChange={setActionScope}
            specificProjectId={specificProjectId}
            onSpecificProjectIdChange={setSpecificProjectId}
            externalFilteredActions={enterpriseAsLocalActions}
            onExternalUpdateAction={enterpriseAsLocalActions ? handleEnterpriseUpdateAction : undefined}
            focusedColumn={actionFocusColumn}
          />
        </div>

        <ToastContainer />

        {/* ── Plan New Action Modal ── */}
        <NewActionModal
          open={showPlanNewActionModal}
          onClose={() => setShowPlanNewActionModal(false)}
          organizationId={organizationId ?? ''}
          planProjectId={planProjectId}
          urlPlanId={urlPlanId}
          planName={activePlanName ?? undefined}
          orgMembers={orgMembers}
          enterpriseProjects={enterpriseProjects}
          defaultAssignedTo={currentUser?.id}
          showToast={showToast}
          onCreated={() => {
            void queryClient.invalidateQueries({ queryKey: ['enterprise-actions-check', organizationId] });
            void queryClient.invalidateQueries({ queryKey: ['step5-evidences-actions', organizationId] });
          }}
        />

        {/* ─── Modal: Novo / Editar Plano de Ação ─────────────────────────────── */}
        {showNewPlanModal && (
          <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={handleCloseNewPlanModal} />
            <div className="relative z-10 flex w-full max-w-2xl flex-col rounded-[4px] border border-[var(--border-default)] bg-[var(--bg-surface)] shadow-[0_24px_48px_rgba(0,0,0,0.28)]">

              {/* Header */}
              <div className="flex shrink-0 items-center justify-between border-b border-[var(--border-default)] px-5 py-3.5">
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-[13px] font-semibold text-[var(--text-primary)]">
                    {editingPlan ? 'Editar Plano de Ação' : 'Novo Plano de Ação'}
                  </span>
                  <p className="text-[11px] text-[var(--text-muted)]">
                    {editingPlan
                      ? 'Atualize o nome, descrição, projeto vinculado ou a equipe deste plano.'
                      : 'Contêiner de ações. Projeto é opcional — planos avulsos são permitidos.'}
                  </p>
                </div>
                <button type="button" onClick={handleCloseNewPlanModal} className="ml-4 shrink-0 rounded-sm p-1 text-[var(--text-muted)] transition-colors hover:bg-[var(--button-hover)] hover:text-[var(--text-primary)]">
                  <X size={15} />
                </button>
              </div>

              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  const fd = new FormData(e.currentTarget);
                  const name = (fd.get('name') as string ?? '').trim();
                  if (!name || !organizationId) return;
                  const projectId = (fd.get('project_id') as string ?? '').trim() || null;
                  const description = (fd.get('description') as string ?? '').trim() || undefined;
                  try {
                    if (editingPlan) {
                      const updated = await ProjectPlanService.updatePlan(organizationId, editingPlan.id, {
                        name,
                        description,
                        project_id: projectId,
                        team_members: planTeamMembers,
                      });
                      void queryClient.invalidateQueries({ queryKey: ['active-plan', urlPlanId, organizationId] });
                      void queryClient.invalidateQueries({ queryKey: ['standalone-plans', organizationId] });
                      void queryClient.invalidateQueries({ queryKey: ['enterprise-projects-list', organizationId] });
                      showToast('success', 'Plano de ação atualizado.');
                      handleCloseNewPlanModal();
                      if (updated.project_id && updated.project_id !== editingPlan.project_id) {
                        navigate(`/acoes?project_id=${updated.project_id}`);
                      }
                    } else {
                      const plan = await ProjectPlanService.createPlan(organizationId, {
                        project_id: projectId,
                        name,
                        type: 'action' as PlanType,
                        description,
                        team_members: planTeamMembers.length > 0 ? planTeamMembers : undefined,
                      });
                      void queryClient.invalidateQueries({ queryKey: ['enterprise-projects-list', organizationId] });
                      void queryClient.invalidateQueries({ queryKey: ['standalone-plans', organizationId] });
                      showToast('success', 'Plano de ação criado com sucesso.');
                      handleCloseNewPlanModal();
                      if (plan.project_id) {
                        navigate(`/acoes?project_id=${plan.project_id}`);
                      } else {
                        navigate(`/acoes?planId=${plan.id}`);
                      }
                    }
                  } catch {
                    showToast('error', editingPlan ? 'Falha ao atualizar plano de ação.' : 'Falha ao criar plano de ação.');
                  }
                }}
                className="flex min-h-0 flex-1 flex-col"
              >
                {/* Two-column body */}
                <div className="grid grid-cols-[3fr_2fr] divide-x divide-[var(--border-default)]">

                  {/* ── Left: Foco do Plano ── */}
                  <div className="flex flex-col gap-5 p-5">

                    {/* Nome */}
                    <div>
                      <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-[var(--text-muted)]">Nome do Plano *</label>
                      <input
                        key={editingPlan?.id ?? 'new'}
                        name="name"
                        required
                        autoFocus
                        defaultValue={editingPlan?.name ?? ''}
                        placeholder="Ex: Plano de Ação Q2 2026"
                        className="w-full rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-app)] px-3 py-2 text-[14px] font-medium text-[var(--text-primary)] placeholder:text-[var(--text-muted)] transition-colors focus:border-blue-500 focus:outline-none"
                      />
                    </div>

                    {/* Descrição — always open */}
                    <div className="flex flex-1 flex-col">
                      <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-[var(--text-muted)]">Descrição</label>
                      <textarea
                        key={`desc-${editingPlan?.id ?? 'new'}`}
                        name="description"
                        rows={6}
                        defaultValue={editingPlan?.description ?? ''}
                        placeholder="Objetivo do plano, escopo, metas e contexto estratégico..."
                        className="w-full flex-1 resize-none rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-app)] px-3 py-2 text-[13px] leading-relaxed text-[var(--text-primary)] placeholder:text-[var(--text-muted)] transition-colors focus:border-blue-500 focus:outline-none"
                      />
                    </div>
                  </div>

                  {/* ── Right: Governança e Equipe ── */}
                  <div className="flex flex-col gap-5 p-5">

                    {/* Projeto */}
                    <div>
                      <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-widest text-[var(--text-muted)]">
                        Projeto
                        <span className="ml-1.5 text-[9px] font-normal normal-case">(opcional)</span>
                      </label>
                      <select
                        key={`proj-${editingPlan?.id ?? 'new'}`}
                        name="project_id"
                        defaultValue={editingPlan?.project_id ?? planProjectId ?? ''}
                        className="w-full rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-app)] px-3 py-2 text-[13px] text-[var(--text-primary)] transition-colors focus:border-blue-500 focus:outline-none"
                      >
                        <option value="">— Nenhum (plano avulso) —</option>
                        {enterpriseProjects.map(p => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Equipe */}
                    <div className="flex flex-1 flex-col">
                      <div className="mb-1.5 flex items-center gap-1.5">
                        <label className="text-[10px] font-semibold uppercase tracking-widest text-[var(--text-muted)]">Equipe</label>
                        <span
                          title="Membros vinculados receberão notificações e farão parte do score de engajamento."
                          className="cursor-help rounded-sm border border-[var(--border-default)] px-1 text-[10px] leading-4 text-[var(--text-muted)]"
                        >
                          ?
                        </span>
                        {planTeamMembers.length > 0 && (
                          <span className="ml-auto text-[10px] text-blue-500">{planTeamMembers.length} selecionado(s)</span>
                        )}
                      </div>

                      {/* Selected badges */}
                      {planTeamMembers.length > 0 && (
                        <div className="mb-2 flex flex-wrap gap-1">
                          {planTeamMembers.map(id => {
                            const m = orgMembers.find(om => om.id === id);
                            if (!m) return null;
                            return (
                              <span
                                key={id}
                                className="inline-flex items-center gap-1 rounded-sm border border-[var(--border-default)] bg-[var(--bg-elevated)] px-2 py-0.5 text-[11px] text-[var(--text-primary)]"
                              >
                                {m.name}
                                <button
                                  type="button"
                                  onClick={() => setPlanTeamMembers(prev => prev.filter(x => x !== id))}
                                  className="rounded-sm p-0.5 text-[var(--text-muted)] transition-colors hover:bg-[var(--button-hover)] hover:text-[var(--text-primary)]"
                                >
                                  <X size={10} />
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      )}

                      {/* Member list */}
                      <div className="flex-1 overflow-hidden rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-app)]">
                        <div className="flex items-center gap-2 border-b border-[var(--border-default)] px-2.5 py-1.5">
                          <Users size={12} className="shrink-0 text-[var(--text-muted)]" />
                          <input
                            type="text"
                            placeholder="Buscar membro..."
                            value={planTeamSearch}
                            onChange={e => setPlanTeamSearch(e.target.value)}
                            className="w-full bg-transparent text-[12px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none"
                          />
                          {planTeamSearch && (
                            <button
                              type="button"
                              onClick={() => setPlanTeamSearch('')}
                              className="shrink-0 text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
                            >
                              <X size={10} />
                            </button>
                          )}
                        </div>
                        <div className="max-h-[160px] overflow-y-auto">
                          {orgMembers.length === 0 ? (
                            <p className="px-3 py-2.5 text-[11px] text-[var(--text-muted)]">Nenhum membro encontrado</p>
                          ) : (
                            orgMembers
                              .filter(m => !planTeamSearch || m.name.toLowerCase().includes(planTeamSearch.toLowerCase()) || m.email.toLowerCase().includes(planTeamSearch.toLowerCase()))
                              .map(member => {
                                const isSelected = planTeamMembers.includes(member.id);
                                const memberInitials = member.name.split(' ').map((w: string) => w[0]).slice(0, 2).join('').toUpperCase();
                                return (
                                  <button
                                    key={member.id}
                                    type="button"
                                    onClick={() => setPlanTeamMembers(prev =>
                                      isSelected ? prev.filter(id => id !== member.id) : [...prev, member.id]
                                    )}
                                    className={`flex w-full items-center gap-2.5 px-3 py-1.5 text-left transition-colors hover:bg-[var(--button-hover)] ${isSelected ? 'bg-blue-500/10' : ''}`}
                                  >
                                    <div className={`flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-sm text-[9px] font-bold ${isSelected ? 'bg-blue-600 text-white' : 'bg-[var(--bg-elevated)] text-[var(--text-secondary)]'}`}>
                                      {isSelected ? <UserCheck size={12} /> : memberInitials}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className="truncate text-[12px] text-[var(--text-primary)]">{member.name}</p>
                                      <p className="truncate text-[10px] text-[var(--text-muted)]">{member.email}</p>
                                    </div>
                                    {isSelected && <Check size={11} className="shrink-0 text-blue-500" />}
                                  </button>
                                );
                              })
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex shrink-0 items-center justify-end gap-2 border-t border-[var(--border-default)] px-5 py-3.5">
                  <button type="button" onClick={handleCloseNewPlanModal} className="rounded-[3px] px-4 py-2 text-[13px] font-medium text-[var(--text-secondary)] transition-colors hover:bg-[var(--button-hover)]">
                    Cancelar
                  </button>
                  <button type="submit" className="rounded-[3px] bg-blue-600 px-5 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-blue-700">
                    {editingPlan ? 'Salvar Alterações' : 'Criar Plano'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </ModuleLayout>
  );
};

export default AcoesPage;
